import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
import { ArrowUpRight } from 'lucide-react';
const categories = [{
  id: 'all',
  label: 'All Projects'
}, {
  id: 'ecommerce',
  label: 'E-Commerce'
}, {
  id: 'business',
  label: 'Business'
}, {
  id: 'mobile',
  label: 'Mobile Apps'
}, {
  id: 'branding',
  label: 'Branding'
}];
const projects = [{
  title: 'KM Stores - Fashion E-Commerce',
  category: 'ecommerce',
  categoryLabel: 'E-Commerce',
  description: 'Full-featured online fashion store with payment integration and responsive design',
  image: 'https://cdn.magicpatterns.com/uploads/i2LhVCtQMkUG6fAfJ3D3h2/KM-Consulting-Portfolio-1-1.jpg'
}, {
  title: 'Electronics Marketplace',
  category: 'ecommerce',
  categoryLabel: 'E-Commerce',
  description: 'Multi-vendor electronics marketplace platform',
  image: 'https://cdn.magicpatterns.com/uploads/2trr769w5X38kcuvdCZNS4/KM-Consulting-Portfolio-2-1.jpg'
}, {
  title: 'Grocery Delivery App',
  category: 'ecommerce',
  categoryLabel: 'E-Commerce',
  description: 'On-demand grocery delivery with real-time tracking',
  image: 'https://images.unsplash.com/photo-1604719312566-8912e9227c6a?q=80&w=2070&auto=format&fit=crop'
}, {
  title: 'FinTech Dashboard',
  category: 'business',
  categoryLabel: 'Web Application',
  description: 'Investment tracking and portfolio management platform',
  image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop'
}, {
  title: 'Corporate Website',
  category: 'business',
  categoryLabel: 'Business Website',
  description: 'Professional corporate presence with CMS integration',
  image: 'https://cdn.magicpatterns.com/uploads/5UzD2SX58E9JFb6awNi8Bq/20221017_045833_0008.png'
}, {
  title: 'Real Estate Platform',
  category: 'business',
  categoryLabel: 'Business Website',
  description: 'Property listing and management system',
  image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2073&auto=format&fit=crop'
}, {
  title: 'WIM Rights - Human Rights Reporting App',
  category: 'mobile',
  categoryLabel: 'Mobile App',
  description: 'Mobile app for reporting human rights violations in the mining sector',
  image: 'https://cdn.magicpatterns.com/uploads/oPFWkCndqKULHtDdRRTt6M/KM-Consulting-Portfolio-6.jpg'
}, {
  title: 'Fitness Tracker',
  category: 'mobile',
  categoryLabel: 'Mobile App',
  description: 'Health and fitness tracking with AI recommendations',
  image: 'https://images.unsplash.com/photo-1576633587382-13ddf37b1fc1?q=80&w=2072&auto=format&fit=crop'
}, {
  title: 'Startup Brand Identity',
  category: 'branding',
  categoryLabel: 'Branding & Web',
  description: 'Complete brand identity and website design',
  image: 'https://images.unsplash.com/photo-1558655146-9f40138edfeb?q=80&w=2064&auto=format&fit=crop'
}];
export function Portfolio() {
  const [activeCategory, setActiveCategory] = useState('all');
  const filteredProjects = activeCategory === 'all' ? projects : projects.filter(p => p.category === activeCategory);
  return <Section id="portfolio" className="bg-[#0B1426] text-white relative" data-id="element-191">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.03]" data-id="element-192" />

      {/* Header */}
      <div className="relative z-10 mb-12" data-id="element-193">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8 mb-12" data-id="element-194">
          <div data-id="element-195">
            <span className="text-[#d8a43e] font-mono text-sm tracking-widest uppercase" data-id="element-196">
              // A Glimpse Into Our Portfolio
            </span>
            <h2 className="mt-3 text-4xl md:text-5xl font-bold text-white" data-id="element-197">
              Our Latest{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d8a43e] to-white" data-id="element-198">
                Projects
              </span>
            </h2>
            <p className="mt-4 text-gray-400 max-w-xl" data-id="element-199">
              We are always available to combine efforts to make your business
              goals a reality. 5+ years experience in e-commerce, business
              websites, and mobile applications.
            </p>
          </div>
          <Button variant="outline" className="font-mono text-xs shrink-0" data-id="element-200">
            View All Projects
          </Button>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2" data-id="element-201">
          {categories.map(cat => <button key={cat.id} onClick={() => setActiveCategory(cat.id)} className={`px-4 py-2 rounded-lg font-mono text-xs uppercase tracking-wider transition-all duration-300 ${activeCategory === cat.id ? 'bg-[#d8a43e] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'}`} data-id="element-202">
              {cat.label}
            </button>)}
        </div>
      </div>

      {/* Projects Grid */}
      <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10" data-id="element-203">
        <AnimatePresence mode="popLayout" data-id="element-204">
          {filteredProjects.map((project, index) => <motion.div key={project.title} layout initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} transition={{
          duration: 0.3,
          delay: index * 0.05
        }} className="group cursor-pointer" data-id="element-205">
              <div className="relative overflow-hidden rounded-xl aspect-[4/3] mb-6 border border-white/10 bg-white/5" data-id="element-206">
                <div className="absolute inset-0 bg-[#0B1426]/40 group-hover:bg-[#0B1426]/0 transition-colors duration-500 z-10" data-id="element-207" />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1426] via-transparent to-transparent opacity-80 z-10" data-id="element-208" />

                <img src={project.image} alt={project.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 filter grayscale group-hover:grayscale-0" data-id="element-209" />

                <div className="absolute top-4 right-4 z-20 bg-[#d8a43e] text-black p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0 duration-300" data-id="element-210">
                  <ArrowUpRight className="w-5 h-5" data-id="element-211" />
                </div>

                {/* Tech Corner Markers */}
                <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-[#d8a43e]/50 z-20" data-id="element-212" />
                <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-[#d8a43e]/50 z-20" data-id="element-213" />

                {/* Category Badge */}
                <div className="absolute bottom-4 left-4 z-20" data-id="element-214">
                  <span className="px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-xs font-mono text-[#d8a43e] border border-[#d8a43e]/30" data-id="element-215">
                    {project.categoryLabel}
                  </span>
                </div>
              </div>

              <div className="border-b border-white/10 pb-4 group-hover:border-[#d8a43e]/50 transition-colors" data-id="element-216">
                <h3 className="text-xl font-bold mb-2 group-hover:text-[#d8a43e] transition-colors" data-id="element-217">
                  {project.title}
                </h3>
                <p className="text-gray-500 text-sm" data-id="element-218">{project.description}</p>
              </div>
            </motion.div>)}
        </AnimatePresence>
      </motion.div>

      {/* Services List */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="mt-20 p-8 rounded-2xl bg-white/[0.02] border border-white/5 relative z-10" data-id="element-219">
        <h3 className="text-xl font-bold text-white mb-6" data-id="element-220">What We Build</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-221">
          {['Ecommerce Website', 'Business Website', 'Mobile Banking', 'Investment Platforms', 'Multi-Level Marketing', 'Digital Marketing', 'Social Media Management', 'Content Creation', 'Software Development', 'Mobile Application', 'UI/UX Design', 'Branding & Graphics'].map((service, i) => <div key={i} className="flex items-center gap-3 text-gray-400 text-sm" data-id="element-222">
              <span className="w-1.5 h-1.5 bg-[#d8a43e] rounded-full" data-id="element-223" />
              {service}
            </div>)}
        </div>
      </motion.div>
    </Section>;
}